import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNodeData } from "../../models/CanvasNode.ts";
import LinkHandler from "./LinkHandler.tsx";
import CarouselDialog from "./CarouselDialog.tsx";
import { pruneStaleBodyEdge } from "../../utils/EdgeCleanup.ts";
import { Database, Settings2, Image as ImageIcon } from 'lucide-react';

interface StaticCardSummary {
    id: string;
    text: string;
    buttons: { id: string; text: string }[];
}

const Carousel: React.FC<any> = (data) => {
    const { nodes, edges, setEdges } = useFlowStore();
    const [showDialog, setShowDialog] = useState(false);

    // Summary state — read-only, refreshed after every save
    const [mode, setMode] = useState<"dynamic" | "static">("dynamic");
    const [body, setBody] = useState("");
    const [arrayAttribute, setArrayAttribute] = useState("");
    const [staticCards, setStaticCards] = useState<StaticCardSummary[]>([]);
    const [dynamicButtons, setDynamicButtons] = useState<{ id: string; text: string }[]>([]);

    useEffect(() => {
        const node = nodes.find(n => n.data.id === data.nodeId);
        if (node) {
            const content = (node.data as CanvasNodeData).content.find(c => c.id === data.subNodeId);
            if (content?.data) {
                const d = content.data as any;
                setMode(d.mode || (d.arrayAttribute ? "dynamic" : "static"));
                setBody(d.text || d.body || "");
                setArrayAttribute(d.arrayAttribute || "");
                setStaticCards(d.cards || []);
                setDynamicButtons(d.buttons || []);
            }
        }
    }, [nodes]);

    const onClose = () => setShowDialog(false);

    // All buttons across all static cards, flattened for handle rendering
    const allButtons = staticCards.flatMap((card, ci) =>
        (card.buttons || []).map(btn => ({ ...btn, cardIndex: ci + 1, cardText: card.text }))
    );
    const hasStaticButtons = mode === 'static' && allButtons.length > 0;
    const hasDynamicButtons = mode === 'dynamic' && dynamicButtons.length > 0;

    // The body-level connector lives at the node header now (see NodeHeader.tsx), not on
    // any individual bubble — but a stale wire from it can still end up pointing at a
    // non-delay target if buttons are added after the fact, so keep pruning that here.
    useEffect(() => {
        pruneStaleBodyEdge(data.nodeId, edges, nodes, setEdges);
    }, [hasStaticButtons, hasDynamicButtons]);

    const buttonConnectionsSection = (buttons: { id: string; text: string; cardIndex?: number }[]) => (
        <div style={{ marginTop: '2px' }}>
            <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                Button Connections
            </div>
            {buttons.map((btn, bi) => (
                <div key={btn.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '4px 0', borderBottom: '1px solid #f1f5f9', position: 'relative'
                }}>
                    <span style={{ fontSize: '10px', color: '#475569' }}>
                        {btn.cardIndex != null
                            ? `Card ${btn.cardIndex} · ${btn.text || 'Button'}`
                            : btn.text || `Button ${bi + 1}`}
                    </span>
                    <LinkHandler idx={btn.id} />
                </div>
            ))}
        </div>
    );

    return (
        <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Compact summary card */}
            <div style={{
                background: '#f8fafc', borderRadius: '10px', padding: '10px 12px',
                border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px'
            }}>
                <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: mode === 'static' ? '#ede9fe' : '#dbeafe',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                    {mode === 'static'
                        ? <Settings2 size={16} color="#6d28d9" />
                        : <Database size={16} color="#1d4ed8" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#0f172a', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {mode === 'static' ? (
                            <><ImageIcon size={11} color="#6d28d9" /> {staticCards.length} card{staticCards.length !== 1 ? 's' : ''} (static)</>
                        ) : (
                            <><Database size={11} color="#1d4ed8" /> {arrayAttribute || 'No attribute set'}</>
                        )}
                    </div>
                    {body ? (
                        <div style={{ fontSize: '10px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {body}
                        </div>
                    ) : (
                        <div style={{ fontSize: '10px', color: '#f59e0b' }}>Body text not set</div>
                    )}
                </div>
            </div>

            <button
                onClick={() => setShowDialog(true)}
                style={{
                    width: '100%', padding: '7px 0', fontSize: '12px', fontWeight: 500,
                    borderRadius: '8px', border: '1px solid var(--primary-bg-color)',
                    background: 'white', color: 'var(--primary-bg-color)', cursor: 'pointer'
                }}
            >
                Configure Carousel
            </button>

            {/* Per-button handles — one per button, each gets its own flow branch */}
            {hasStaticButtons && buttonConnectionsSection(allButtons)}
            {hasDynamicButtons && buttonConnectionsSection(dynamicButtons)}

            <Dialog
                header="Configure Carousel"
                visible={showDialog}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.5)", zIndex: 9998 }}
                contentStyle={{ zIndex: 9999, padding: '16px 20px' }}
                style={{ width: "580px", maxWidth: "95vw" }}
                onHide={onClose}
            >
                <CarouselDialog data={data} onClose={onClose} />
            </Dialog>
        </div>
    );
};

export default Carousel;
